import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, realtimeDb } from './firebase'; // adjust path if needed
import {
  User,
  CreditCard,
  LogOut,
  Loader2,
  ArrowLeft,
  Zap,
  Shield,
  ExternalLink,
  Mail
} from 'lucide-react';

export default function Settings() {
  const navigate = useNavigate();

  // State
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingLoading, setBillingLoading] = useState(false);
  const [error, setError] = useState('');

  // 1. Auth & Profile Fetch
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!currentUser) {
          navigate('/auth');
          return;
        }

        setUser(currentUser);

        // Fetch profile from Realtime DB
        const infoRef = ref(realtimeDb, `users/${currentUser.uid}`);
        const snapshot = await get(infoRef);

        if (snapshot.exists()) {
          setUserProfile(snapshot.val());
        } else {
          setUserProfile(null);
        }
      } catch (e) {
        console.error('Error fetching profile:', e);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // 2. Manage Billing
  const handleManageBilling = async () => {
    if (!user) return;

    setBillingLoading(true);
    setError('');

    try {
      const BACKEND_URL = process.env.REACT_APP_CORE_SERVER_URL;
      if (!BACKEND_URL) {
        throw new Error('Backend URL not configured');
      }

      const token = await user.getIdToken();

      const response = await fetch(`${BACKEND_URL}/manage-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'Could not load billing portal.');
      }
    } catch (err) {
      console.error('Billing Error:', err);

      if (
        err.message?.includes('inactive') ||
        err.message?.includes('No active')
      ) {
        setError("You don't have an active subscription to manage.");
      } else {
        setError('Failed to load billing settings. Please try again.');
      }
    } finally {
      setBillingLoading(false);
    }
  };

  // 3. Logout
  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-500 w-10 h-10" />
      </div>
    );
  }

  const isPremium = userProfile?.tier === 'premium';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-blue-500 selection:text-white">
      {/* NAVBAR */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img
                src="/logo512.png"
                alt="Logo"
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-bold text-lg text-white tracking-tight hidden md:block">
                BloxBuddy
              </span>
            </div>
            <div className="h-6 w-px bg-slate-800 mx-2 hidden md:block" />
            <span className="font-medium text-slate-400 text-sm">
              Account Settings
            </span>
          </div>

          <button
            onClick={() => navigate('/console')}
            className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition-all bg-slate-900 hover:bg-slate-800 px-4 py-2 rounded-full border border-slate-800"
          >
            <ArrowLeft size={16} />{' '}
            <span className="hidden sm:inline">Back to Console</span>
          </button>
        </div>
      </nav>

      {/* CONTENT */}
      <div className="pt-32 pb-20 px-6 max-w-3xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-slate-400">
            Manage your profile, subscription, and usage credits.
          </p>
        </div>

        {/* PROFILE */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center border border-slate-700">
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt="User"
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <User size={32} className="text-slate-400" />
                )}
              </div>

              <div>
                <h2 className="text-xl font-bold text-white">
                  {user?.displayName || 'User'}
                </h2>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Mail size={14} /> {user?.email}
                </div>
              </div>
            </div>

	  {/*   <div
              className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
                isPremium
                  ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                  : 'bg-slate-800 text-slate-400 border-slate-700'
              }`}
            >
              {isPremium ? 'Premium Plan' : 'Free Plan'}
            </div>
	    */}
          </div>
        </div>

        {/* CREDITS */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8">
          <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Zap size={20} className="text-yellow-400" /> Usage Credits
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
              <p className="text-slate-500 text-xs font-bold uppercase mb-1">
                Basic Credits
              </p>
              <div className="text-2xl font-mono font-bold text-white">
                {(userProfile?.credits?.basic || 0).toLocaleString()}
              </div>
            </div>

            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 relative">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Shield size={64} />
              </div>
              <p className="text-cyan-400 text-xs font-bold uppercase mb-1">
                Diamond Credits
              </p>
              <div className="text-2xl font-mono font-bold text-white">
                {(userProfile?.credits?.diamond || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>

        {/* BILLING */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <CreditCard size={20} className="text-blue-400" /> Subscription &
            Billing
          </h3>

	  <div className="mb-4 p-3 bg-green-900/20 border border-green-500/30 text-white-200 text-sm rounded-lg">              <h3> Use the link on your email to manage your billing.  </h3>                                          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 text-red-200 text-sm rounded-lg">
              {error}
            </div>
          )}

	  {/* <button
            onClick={handleManageBilling}
            disabled={billingLoading}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {billingLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <ExternalLink size={18} />
            )}
            {isPremium ? 'Manage Subscription' : 'View Billing Portal'}
          </button>
	  */}

	  <button                                              onClick={()=> navigate('/pricing')}                         
	className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 px-6 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"                                                    >                                                        <ExternalLink size={18} />                                              Upgrade plan                                                   </button>

	  
        </div>

        {/* LOGOUT */}
        <div className="pt-8 border-t border-slate-800/50">
          <button
            onClick={handleLogout}
            className="text-slate-500 hover:text-red-400 transition-colors flex items-center gap-2 text-sm font-medium px-2"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
